import JsBarcode from 'jsbarcode';

/**
 * Barcode label rendering + printing. The barcode value is always the item's
 * SKU (POS scanning resolves scans by SKU). Labels are printed through the
 * browser with an exact @page size, so the same markup drives the on-screen
 * preview and the printout.
 */

// Physical sizes in mm. Sheet presets lay labels out on A4 sticker sheets.
export const LABEL_PRESETS = {
  // Sticker rolls on an ordinary 80 mm receipt printer: one long continuous
  // strip as wide as the roll, labels stacked down it. Size/gap are set by the
  // user (ROLL80_DEFAULTS) since sticker rolls vary.
  'roll80': { label: '80 mm receipt printer – sticker roll (continuous)', w: 70, h: 40, continuous: true },
  '50x30': { label: '50 × 30 mm (thermal roll)', w: 50, h: 30 },
  '50x25': { label: '50 × 25 mm (thermal roll)', w: 50, h: 25, compact: true },
  '60x40': { label: '60 × 40 mm (thermal roll)', w: 60, h: 40, large: true },
  'a4-24': { label: 'A4 sheet, 24 per page (70 × 37 mm)', w: 70, h: 37, sheet: { cols: 3, rows: 8, top: 0.5, left: 0 } },
  'a4-40': { label: 'A4 sheet, 40 per page (52.5 × 29.7 mm)', w: 52.5, h: 29.7, sheet: { cols: 4, rows: 10, top: 0, left: 0 } },
};

export const DEFAULT_PRESET = '50x30';

/** Starting values for the 80 mm continuous roll; all adjustable in the print window. */
export const ROLL80_DEFAULTS = { w: 70, h: 40, gap: 3, offset: 0 };
const ROLL_WIDTH_MM = 80;

/**
 * The label size/layout to use: a fixed preset, or for the continuous roll
 * the preset merged with the user's measurements.
 */
export function resolvePreset(presetKey, roll = null) {
  const p = typeof presetKey === 'string' ? LABEL_PRESETS[presetKey] : presetKey;
  if (!p || !p.continuous) return p;
  const r = { ...ROLL80_DEFAULTS, ...(roll || {}) };
  const w = Math.min(Math.max(Number(r.w) || ROLL80_DEFAULTS.w, 20), ROLL_WIDTH_MM);
  const h = Math.max(Number(r.h) || ROLL80_DEFAULTS.h, 10);
  return {
    ...p, w, h,
    gap: Math.max(Number(r.gap) || 0, 0),
    offset: Number(r.offset) || 0,
    compact: h < 28,
    large: h >= 38,
  };
}

// Horizontal padding inside every label, each side.
const PAD_X_MM = 2;
// Quiet zone each side, in modules (narrowest bar widths). GS1 asks for
// 10x for Code 128 and 11x/7x for EAN-13 — 11 covers both.
const QUIET_ZONE_MODULES = 11;
// Below this a typical 203 dpi thermal printer (0.125 mm dots) can't
// reproduce bar widths reliably and handheld scanners start missing reads.
export const MIN_MODULE_MM = 0.25;

/** Renders the barcode as an SVG string scaled to fill its box. Returns null if the value can't be encoded. */
export function renderBarcodeSvg(value, format) {
  if (!value) return null;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  let valid = true;
  try {
    JsBarcode(svg, value, {
      format: format || 'CODE128',
      width: 1,                 // 1 user unit per module, so viewBox width = total modules
      height: 50,
      margin: 0,
      marginLeft: QUIET_ZONE_MODULES,
      marginRight: QUIET_ZONE_MODULES,
      displayValue: false,      // the number is printed as HTML text underneath
      flat: true,
      valid: (ok) => { valid = ok; },
    });
  } catch (e) {
    return null;
  }
  if (!valid) return null;

  const modules = parseFloat(svg.getAttribute('width'));
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.removeAttribute('style');
  // Stretch vertically only; horizontal scale stays uniform so bar ratios hold.
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('data-modules', String(modules));
  return svg.outerHTML;
}

/** Width of one module in mm for this value on this label size. */
export function moduleSizeMm(svgHtml, presetKey) {
  const preset = resolvePreset(presetKey);
  const m = svgHtml && svgHtml.match(/data-modules="([\d.]+)"/);
  if (!preset || !m) return null;
  return (preset.w - PAD_X_MM * 2) / parseFloat(m[1]);
}

/** "KSh 1,250" for Kenyan shillings, otherwise the company's currency. */
export function formatLabelPrice(amount, currency) {
  const value = Number(amount) || 0;
  const code = currency && currency !== 'null' ? currency : 'KES';
  // Whole amounts print as "KSh 100"; fractional ones keep both decimals.
  const decimals = Number.isInteger(value) ? 0 : 2;
  if (code === 'KES') {
    return 'KSh ' + value.toLocaleString('en', { minimumFractionDigits: decimals, maximumFractionDigits: 2 });
  }
  return value.toLocaleString('en', { style: 'currency', currency: code, minimumFractionDigits: decimals, maximumFractionDigits: 2 });
}

function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** One label's inner markup. Content is deliberately limited to: business, product, price, barcode, number. */
export function labelHtml({ businessName, name, price, barcode, svg }) {
  return `<div class="bl-label">
  <div class="bl-biz">${escapeHtml(businessName)}</div>
  <div class="bl-name">${escapeHtml(name)}</div>
  <div class="bl-price">${escapeHtml(price)}</div>
  <div class="bl-bars">${svg || ''}</div>
  <div class="bl-num">${escapeHtml(barcode)}</div>
</div>`;
}

/** CSS shared by the preview and the print document. */
export function labelCss(presetKey) {
  const p = resolvePreset(presetKey);
  const scale = p.large ? 1.2 : 1;
  const pt = (n) => (n * scale).toFixed(1) + 'pt';
  return `
.bl-label { box-sizing: border-box; width: ${p.w}mm; height: ${p.h}mm; padding: 1.2mm ${PAD_X_MM}mm 0.8mm;
  display: flex; flex-direction: column; align-items: center; text-align: center; overflow: hidden;
  font-family: Arial, Helvetica, sans-serif; color: #000; background: #fff; }
.bl-biz, .bl-name, .bl-price, .bl-num { flex: 0 0 auto; }
.bl-biz { width: 100%; font-size: ${pt(6.5)}; font-weight: 700; text-transform: uppercase; letter-spacing: .3px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; line-height: 1.2; }
.bl-name { width: 100%; font-size: ${pt(7.5)}; line-height: 1.15; overflow: hidden; display: -webkit-box;
  -webkit-box-orient: vertical; -webkit-line-clamp: ${p.compact ? 1 : 2}; word-break: break-word;
  max-height: ${p.compact ? 1.15 : 2.3}em; }
.bl-price { font-size: ${pt(p.compact ? 9 : 10)}; font-weight: 700; line-height: 1.2; }
.bl-bars { flex: 1 1 auto; min-height: 6mm; width: 100%; margin-top: 0.5mm; }
.bl-bars svg { display: block; width: 100%; height: 100%; }
.bl-num { font-family: 'Courier New', monospace; font-size: ${pt(7)}; letter-spacing: .5px; line-height: 1.1; }
`;
}

/** Full print document: one label per page on rolls, a grid per page on A4 sheets. */
export function buildPrintDocument(presetKey, labels) {
  const p = resolvePreset(presetKey);
  let body;
  let pageCss;

  if (p.continuous) {
    // One page exactly as tall as all the labels: no page breaks, and taller
    // than wide so the browser never turns it landscape. Labels are centred
    // on the 80 mm roll (plus any offset the user set).
    const pitch = p.h + p.gap;
    const height = Math.max(pitch * labels.length, p.h);
    const left = Math.max((ROLL_WIDTH_MM - p.w) / 2 + p.offset, 0);
    body = `<div class="bl-roll">${labels.map((l) => `<div class="bl-slot">${l}</div>`).join('')}</div>`;
    pageCss = `@page { size: ${ROLL_WIDTH_MM}mm ${height.toFixed(1)}mm; margin: 0; }
.bl-roll { width: ${ROLL_WIDTH_MM}mm; }
.bl-slot { height: ${pitch}mm; padding-left: ${left.toFixed(1)}mm; box-sizing: border-box; overflow: hidden;
  break-inside: avoid; page-break-inside: avoid; }`;
  } else if (p.sheet) {
    const perPage = p.sheet.cols * p.sheet.rows;
    const pages = [];
    for (let i = 0; i < labels.length; i += perPage) {
      pages.push(`<div class="bl-sheet">${labels.slice(i, i + perPage).join('')}</div>`);
    }
    body = pages.join('');
    pageCss = `@page { size: A4; margin: 0; }
.bl-sheet { width: 210mm; height: 297mm; box-sizing: border-box; padding: ${p.sheet.top}mm 0 0 ${p.sheet.left}mm;
  display: grid; grid-template-columns: repeat(${p.sheet.cols}, ${p.w}mm); grid-auto-rows: ${p.h}mm;
  page-break-after: always; break-after: page; overflow: hidden; }`;
  } else {
    body = labels.map((l) => `<div class="bl-page">${l}</div>`).join('');
    pageCss = `@page { size: ${p.w}mm ${p.h}mm; margin: 0; }
.bl-page { width: ${p.w}mm; height: ${p.h}mm; overflow: hidden; page-break-after: always; break-after: page; }`;
  }

  return `<!doctype html><html><head><meta charset="utf-8"><title>Barcode labels</title><style>
html, body { margin: 0; padding: 0; background: #fff; }
* { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
${pageCss}
${labelCss(presetKey)}
</style></head><body>${body}</body></html>`;
}

/** Prints via a hidden iframe so the app page itself is untouched. */
export function printDocument(html) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  const cleanup = () => setTimeout(() => iframe.remove(), 1000);
  setTimeout(() => {
    iframe.contentWindow.focus();
    iframe.contentWindow.onafterprint = cleanup;
    iframe.contentWindow.print();
    // Some browsers never fire afterprint for iframes.
    setTimeout(cleanup, 60000);
  }, 250);
}
